import React, { useState } from "react";

import { createClient } from "../../api";

// Add a customer without leaving a half-written invoice.
//
// Deliberately small: name, and the fields the invoice actually prints.
// Group, fiscal month end, status and rates are edited on the client page,
// where they belong. This form exists so a first invoice can be PRINTED, not
// so a client can be fully configured.

const FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "bill_to", label: "Bill to" },
  { key: "street_address", label: "Street address" },
  { key: "city", label: "City" },
  { key: "province", label: "Province" },
  { key: "postal_code", label: "Postal code" },
  { key: "email", label: "Email" },
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

export default function AddClientInline({
  initialName = "", existingClients = [], onCreated, onCancel,
}) {
  const [values, setValues] = useState({ ...EMPTY, name: initialName });
  const [error, setError] = useState("");
  const [collision, setCollision] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    const name = values.name.trim();
    setError("");
    setCollision(null);

    if (!name) {
      setError("Name is required.");
      return;
    }

    // Checked here as well as by the server. The server's rule is the
    // authority, but it can only answer after a round trip and its 400 cannot
    // offer the one thing the user wants -- that client, selected.
    const clash = existingClients.find(
      (c) => c.is_active && c.name.toLowerCase() === name.toLowerCase(),
    );
    if (clash) {
      setCollision(clash);
      return;
    }

    setBusy(true);
    try {
      const created = await createClient({ ...values, name });
      onCreated(created);
    } catch (e) {
      const detail = e && e.response && e.response.data;
      const nameErrors = detail && detail.name;
      if (nameErrors && String(nameErrors).toLowerCase().includes("already exists")) {
        // Created by someone else since this page loaded its client list, so
        // there is no local row to offer -- say so and let them retype.
        setError(`${name} already exists. Close this and search for it.`);
      } else {
        setError("Could not create the customer.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      // This form renders INSIDE the invoice's own <form onSubmit={saveInvoice}>,
      // so a bare Enter in any of these inputs submits the invoice. Enter here
      // means "save the customer", which is what it looks like it means.
      onKeyDown={(e) => {
        if (e.key !== "Enter" || e.target.tagName !== "INPUT") return;
        e.preventDefault();
        if (!busy) save();
      }}
      style={{
        border: "1px solid #E5E7EB", borderRadius: 6, padding: 12,
        marginTop: 6, background: "#F9FAFB",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>New customer</div>

      {FIELDS.map((f) => (
        <label key={f.key} style={{ display: "block", marginBottom: 6 }}>
          <span style={{ display: "block", fontSize: 11, color: "#6B7280" }}>
            {f.label}
          </span>
          <input
            aria-label={f.label}
            value={values[f.key]}
            onChange={set(f.key)}
            style={{
              width: "100%", padding: "6px 8px",
              border: "1px solid #D1D5DB", borderRadius: 4,
            }}
          />
        </label>
      ))}

      {collision ? (
        <div style={{ color: "#B45309", fontSize: 13, marginBottom: 8 }}>
          {collision.name} already exists.{" "}
          <button
            type="button"
            onClick={() => onCreated(collision)}
            style={{
              background: "none", border: "none", color: "#1D4ED8",
              textDecoration: "underline", cursor: "pointer", font: "inherit",
            }}
          >
            Select it instead
          </button>
        </div>
      ) : null}

      {error ? (
        <div style={{ color: "#B91C1C", fontSize: 13, marginBottom: 8 }}>{error}</div>
      ) : null}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={save} disabled={busy}>
          Save customer
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
